import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Save, RotateCcw, Plus, Trash2, X, Edit3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { plannerBucket } from './plannerBucket';

// Yatay kaydırmada solda donan sütun. 🔴 Arka plan MUTLAKA OPAK olmalı:
// eskiden satır `bg-slate-50/40` (%40 saydam) idi ve hücre `bg-inherit` aldığı
// için donmuş sütun da saydam kalıyor, altından kayan sayılar görünüyordu.
// Sağdaki çizgi + gölge, içeriğin sütunun ALTINA girdiğini belli eder; yoksa
// üst üste binmiş gibi duruyor.
// Gölge her zeminde çalışır; ayırıcı çizginin RENGİ zemine göre verilir
// (açık satırda slate-200, koyu başlık/toplam satırında slate-600 — aksi halde
// koyu zeminde açık bir çizgi sırıtıyor).
const DONUK_SUTUN = 'sticky left-0 border-r shadow-[2px_0_4px_-2px_rgba(15,23,42,0.15)]';

const MONTHS = [
  { idx: 1, label: 'January' },
  { idx: 2, label: 'February' },
  { idx: 3, label: 'March' },
  { idx: 4, label: 'April' },
  { idx: 5, label: 'May' },
  { idx: 6, label: 'June' },
  { idx: 7, label: 'July' },
  { idx: 8, label: 'August' },
  { idx: 9, label: 'September' },
  { idx: 10, label: 'October' },
  { idx: 11, label: 'November' },
  { idx: 12, label: 'December' },
];

const EMPTY_VALUES = Object.fromEntries(MONTHS.map(m => [m.idx, 0]));

const SPLIT_TARGET_BRAND_IDS = new Set([
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7',
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e',
  'dfcf54dd-b00d-434a-be1e-27b6484848eb',
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f',
]);

const LEGACY_DEFAULT_ROW_DEFS = [
  { key: 'google_ads', label: 'Google Ads', metricType: 'spend' },
  { key: 'meta_ads', label: 'Meta Ads', metricType: 'spend' },
  { key: 'analytics_revenue', label: 'Analytics Revenue Target', metricType: 'revenue' },
];

const SPLIT_DEFAULT_ROW_DEFS = [
  { key: 'google_ads_performance', label: 'Google Ads - Performance Budget', metricType: 'spend' },
  { key: 'google_ads_branding', label: 'Google Ads - Branding Budget', metricType: 'spend' },
  { key: 'meta_ads_performance', label: 'Meta Ads - Performance Budget', metricType: 'spend' },
  { key: 'meta_ads_branding', label: 'Meta Ads - Branding Budget', metricType: 'spend' },
  { key: 'analytics_revenue_performance', label: 'Analytics - Performance Revenue Target', metricType: 'revenue' },
  { key: 'analytics_revenue_branding', label: 'Analytics - Branding Revenue Target', metricType: 'revenue' },
];

const EMPTY_METRICS = Object.fromEntries(MONTHS.map(m => [m.idx, { gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, satis_adedi: 0, satis_degeri: 0 }]));
const toDefaultRows = (defs) => defs.map((d) => ({
  ...d,
  isSystem: true,
  values: { ...EMPTY_VALUES },
  metricsValues: { ...EMPTY_METRICS },
}));

const SYSTEM_ROW_LOGOS = {
  google_ads: '/google_ads-512.webp',
  meta_ads: '/6033716.png',
  analytics_revenue: '/GA4logoicon.svg',
  google_ads_performance: '/google_ads-512.webp',
  google_ads_branding: '/google_ads-512.webp',
  meta_ads_performance: '/6033716.png',
  meta_ads_branding: '/6033716.png',
  analytics_revenue_performance: '/GA4logoicon.svg',
  analytics_revenue_branding: '/GA4logoicon.svg',
};
const API = 'https://api.glowytics.com';

const fmtCurrency = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(Number(v || 0));

const rowTotal = (row) => MONTHS.reduce((sum, m) => sum + Number(row.values?.[m.idx] || 0), 0);
const monthlyTotalByType = (rows, metricType, monthIdx) => rows
  .filter(r => r.metricType === metricType)
  .reduce((sum, r) => sum + Number(r.values?.[monthIdx] || 0), 0);

const BRANDING_METRICS = [
  { key: 'gosterim', label: 'Gösterim' },
  { key: 'erisim', label: 'Erişim' },
  { key: 'izlenme', label: 'İzlenme' },
  { key: 'trafik', label: 'Trafik' },
];

const PERFORMANCE_METRICS = [
  { key: 'trafik', label: 'Trafik' },
  { key: 'satis_adedi', label: 'Satış Adedi' },
  { key: 'satis_degeri', label: 'Satış Değeri' },
];

const metricsYearlyTotal = (row, metrics = BRANDING_METRICS) => {
  const mv = row.metricsValues || {};
  return metrics.reduce((acc, { key }) => {
    let sum = 0;
    for (let m = 1; m <= 12; m++) sum += Number(mv[m]?.[key] ?? mv[String(m)]?.[key] ?? 0);
    acc[key] = sum;
    return acc;
  }, {});
};

// trafik (sessions) and satis_degeri (revenue) come from every GA4 source; satis_adedi
// (transactions) only when the brand has a BigQuery GA4 feed (hasTxn).
const isGa4FedKey = (key, mappingsLen, hasTxn) =>
  mappingsLen > 0 && (key === 'trafik' || key === 'satis_degeri' || (key === 'satis_adedi' && hasTxn));
const smKey = (s, m) => `${String(s ?? '').trim().toLowerCase()}|${String(m ?? '').trim().toLowerCase()}`;

// Reusable editor: pick / type GA4 source-medium pairs that feed a manual line's
// trafik (sessions) / satış adedi (transactions) / ciro (revenue) live from GA4.
function Ga4MappingEditor({ brandId, year, value, onChange, onMeta }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasTxn, setHasTxn] = useState(false);
  const [manualSource, setManualSource] = useState('');
  const [manualMedium, setManualMedium] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!brandId || brandId === 'unknown-brand') return;
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans/ga4-source-mediums?year=${year}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            'X-Brand-ID': brandId,
          },
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          setOptions(Array.isArray(data?.sourceMediums) ? data.sourceMediums : []);
          setHasTxn(!!data?.hasTransactions);
          if (onMeta) onMeta({ hasTransactions: !!data?.hasTransactions });
        }
      } catch (e) {
        if (!cancelled) { setOptions([]); setHasTxn(false); if (onMeta) onMeta({ hasTransactions: false }); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [brandId, year, onMeta]);

  const mappings = Array.isArray(value) ? value : [];
  const addMapping = (source, medium) => {
    const s = String(source || '').trim();
    const m = String(medium || '').trim();
    if (!s && !m) return;
    if (mappings.some(x => smKey(x.source, x.medium) === smKey(s, m))) return;
    onChange([...mappings, { source: s, medium: m }]);
  };
  const removeMapping = (idx) => onChange(mappings.filter((_, i) => i !== idx));

  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-emerald-800">GA4 eşleştirme (opsiyonel)</p>
        {mappings.length > 0 && (
          <span className="text-[10px] text-emerald-700">
            {hasTxn ? 'Trafik / Satış Adedi / Ciro' : 'Trafik / Ciro'} GA4'ten otomatik gelecek
          </span>
        )}
      </div>
      {!loading && options.length > 0 && !hasTxn && (
        <p className="text-[10px] text-amber-600">Not: Bu markada GA4 transaction verisi yok — Satış Adedi elle girilebilir kalır.</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <select
          value=""
          disabled={loading}
          onChange={(e) => {
            const opt = options[Number(e.target.value)];
            if (opt) addMapping(opt.source, opt.medium);
          }}
          className="text-[11px] border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-slate-400 bg-white min-w-[240px]"
        >
          <option value="">{loading ? 'GA4 yükleniyor…' : (options.length ? 'GA4\'ten source/medium seç…' : 'GA4 verisi yok — elle yaz')}</option>
          {options.map((o, i) => (
            <option key={`${smKey(o.source, o.medium)}-${i}`} value={i}>
              {(o.source || '(not set)')} / {(o.medium || '(not set)')} · {Number(o.sessions || 0).toLocaleString('tr-TR')} oturum
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={manualSource}
          onChange={(e) => setManualSource(e.target.value)}
          placeholder="source (örn: akakce.com)"
          className="text-[11px] border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-slate-400 w-40"
        />
        <span className="text-slate-400 text-[11px]">/</span>
        <input
          value={manualMedium}
          onChange={(e) => setManualMedium(e.target.value)}
          placeholder="medium (örn: referral)"
          className="text-[11px] border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-slate-400 w-40"
        />
        <button
          type="button"
          onClick={() => { addMapping(manualSource, manualMedium); setManualSource(''); setManualMedium(''); }}
          className="text-[11px] px-2.5 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
        >
          + Ekle
        </button>
      </div>

      {mappings.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap pt-1">
          {mappings.map((mp, i) => (
            <span key={`${smKey(mp.source, mp.medium)}-${i}`} className="inline-flex items-center gap-1 text-[10px] bg-white border border-emerald-300 text-emerald-800 rounded-full px-2 py-0.5">
              {(mp.source || '(not set)')} / {(mp.medium || '(not set)')}
              <button type="button" onClick={() => removeMapping(i)} className="text-emerald-500 hover:text-emerald-700">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function AddManualLineModal({ isSplitTargetBrand, brandId, year, onClose, onSave }) {
  const [label, setLabel] = useState('');
  const [metricType, setMetricType] = useState('spend');
  const [bucket, setBucket] = useState(isSplitTargetBrand ? 'branding' : 'general');
  const [ga4Mappings, setGa4Mappings] = useState([]);
  const [ga4HasTxn, setGa4HasTxn] = useState(false);
  const [values, setValues] = useState({ ...EMPTY_VALUES });
  const [metricsValues, setMetricsValues] = useState(() => {
    const m = {};
    for (let i = 1; i <= 12; i++) m[i] = { gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, satis_adedi: 0, satis_degeri: 0 };
    return m;
  });

  const handleSave = () => {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error('Label gerekli');
      return;
    }
    const b = isSplitTargetBrand ? bucket : 'general';
    const key = `custom_${b}_${Date.now()}`;
    onSave({
      key,
      label: trimmed,
      metricType: metricType === 'revenue' ? 'revenue' : 'spend',
      isSystem: false,
      values: { ...values },
      metricsValues: { ...metricsValues },
      ga4SourceMedium: ga4Mappings,
    });
    toast.success('Satır eklendi');
  };

  const updateVal = (month, v) => {
    const n = Number(v);
    const safe = Number.isFinite(n) && n >= 0 ? n : 0;
    setValues(prev => ({ ...prev, [month]: safe }));
  };

  const updateMetric = (month, metricKey, v) => {
    const n = Number(v);
    const safe = Number.isFinite(n) && n >= 0 ? n : 0;
    setMetricsValues(prev => ({
      ...prev,
      [month]: { ...(prev[month] || {}), [metricKey]: safe },
    }));
  };

  const isBranding = metricType === 'spend' && (bucket === 'branding' || !isSplitTargetBrand);
  const isPerformance = isSplitTargetBrand && bucket === 'performance' && metricType === 'spend';
  const metricsToShow = isBranding ? BRANDING_METRICS : isPerformance ? PERFORMANCE_METRICS : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Manuel Satır Ekle</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Satır adı</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Örn: Yandex, Pinterest, Medya Satınalma"
              className="w-full text-[12px] border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-slate-400"
            />
          </div>
          <div className="flex gap-4">
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">Tip</label>
              <select
                value={metricType}
                onChange={(e) => setMetricType(e.target.value)}
                className="text-[12px] border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-slate-400 bg-white"
              >
                <option value="spend">Spend (Bütçe)</option>
                <option value="revenue">Revenue</option>
              </select>
            </div>
            {isSplitTargetBrand && (
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Kategori</label>
                <select
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value)}
                  className="text-[12px] border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-slate-400 bg-white"
                >
                  <option value="performance">Performance</option>
                  <option value="branding">Branding</option>
                </select>
              </div>
            )}
          </div>
          <Ga4MappingEditor brandId={brandId} year={year} value={ga4Mappings} onChange={setGa4Mappings} onMeta={(m) => setGa4HasTxn(!!m.hasTransactions)} />
          <div>
            <p className="text-[11px] font-medium text-slate-600 mb-2">Hangi ay için eklemek istiyorsun? (her ay ayrı)</p>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-2 py-1.5 text-left font-medium">Ay</th>
                    <th className="px-2 py-1.5 text-right font-medium">Bütçe</th>
                    {metricsToShow.map(bm => (
                      <th key={bm.key} className="px-2 py-1.5 text-right font-medium">{bm.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MONTHS.map(m => (
                    <tr key={m.idx} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-2 py-1.5 font-medium text-slate-700">{m.label}</td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={values[m.idx] ?? 0}
                          onChange={(e) => updateVal(m.idx, e.target.value)}
                          className="w-20 text-right border border-slate-200 rounded px-2 py-1 outline-none focus:border-slate-400"
                        />
                      </td>
                      {metricsToShow.map(bm => {
                        const ga4Fed = isGa4FedKey(bm.key, ga4Mappings.length, ga4HasTxn);
                        return (
                        <td key={bm.key} className="px-2 py-1.5">
                          <input
                            type="number"
                            min="0"
                            step={bm.key === 'satis_degeri' ? '0.01' : '1'}
                            value={ga4Fed ? '' : (metricsValues[m.idx]?.[bm.key] ?? 0)}
                            disabled={ga4Fed}
                            placeholder={ga4Fed ? 'GA4' : undefined}
                            onChange={(e) => updateMetric(m.idx, bm.key, e.target.value)}
                            title={ga4Fed ? 'GA4 eşleştirmesinden otomatik gelir' : undefined}
                            className={`w-16 text-right border border-slate-200 rounded px-2 py-1 outline-none ${ga4Fed ? 'bg-emerald-50 text-emerald-400 cursor-not-allowed placeholder:text-emerald-400' : 'focus:border-slate-400'}`}
                          />
                        </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
            İptal
          </button>
          <button onClick={handleSave} className="px-3 py-1.5 text-[11px] font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-700">
            Kaydet ve Ekle
          </button>
        </div>
      </div>
    </div>
  );
}

function EditMetricsModal({ row, brandId, year, onClose, onSave }) {
  const isPerformance = row?.key?.includes('_performance');
  const metricsToEdit = isPerformance ? PERFORMANCE_METRICS : BRANDING_METRICS;
  const canMapGa4 = row && !row.isSystem;

  const [ga4Mappings, setGa4Mappings] = useState(() => (Array.isArray(row?.ga4SourceMedium) ? row.ga4SourceMedium : []));
  const [ga4HasTxn, setGa4HasTxn] = useState(false);

  const [metricsValues, setMetricsValues] = useState(() => {
    const mv = row?.metricsValues || {};
    const m = {};
    for (let i = 1; i <= 12; i++) {
      m[i] = { gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, satis_adedi: 0, satis_degeri: 0 };
      const d = mv[i] || mv[String(i)];
      if (d) m[i] = { ...m[i], ...d };
    }
    return m;
  });

  const updateMetric = (month, metricKey, v) => {
    const n = Number(v);
    const safe = Number.isFinite(n) && n >= 0 ? n : 0;
    setMetricsValues(prev => ({
      ...prev,
      [month]: { ...(prev[month] || {}), [metricKey]: safe },
    }));
  };

  const handleSave = () => {
    onSave({ metricsValues, ga4SourceMedium: ga4Mappings });
  };

  if (!row) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Aylık metrikler: {row.label}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <p className="text-[11px] text-slate-500 mb-3">
            {isPerformance ? 'Trafik, Satış Adedi, Satış Değeri — Performance hedefleri' : 'Gösterim, Erişim, İzlenme, Trafik — Report 2 için'}
          </p>
          {canMapGa4 && (
            <div className="mb-3">
              <Ga4MappingEditor brandId={brandId} year={year} value={ga4Mappings} onChange={setGa4Mappings} onMeta={(m) => setGa4HasTxn(!!m.hasTransactions)} />
            </div>
          )}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-2 py-1.5 text-left font-medium">Ay</th>
                  {metricsToEdit.map(bm => (
                    <th key={bm.key} className="px-2 py-1.5 text-right font-medium">{bm.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MONTHS.map(m => (
                  <tr key={m.idx} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-2 py-1.5 font-medium text-slate-700">{m.label}</td>
                    {metricsToEdit.map(bm => {
                      const ga4Fed = canMapGa4 && isGa4FedKey(bm.key, ga4Mappings.length, ga4HasTxn);
                      return (
                      <td key={bm.key} className="px-2 py-1.5">
                        <input
                          type="number"
                          min="0"
                          step={bm.key === 'satis_degeri' ? '0.01' : '1'}
                          value={ga4Fed ? '' : (metricsValues[m.idx]?.[bm.key] ?? 0)}
                          disabled={ga4Fed}
                          placeholder={ga4Fed ? 'GA4' : undefined}
                          title={ga4Fed ? 'GA4 eşleştirmesinden otomatik gelir' : undefined}
                          onChange={(e) => updateMetric(m.idx, bm.key, e.target.value)}
                          className={`w-20 text-right border border-slate-200 rounded px-2 py-1 outline-none ${ga4Fed ? 'bg-emerald-50 text-emerald-400 cursor-not-allowed placeholder:text-emerald-400' : 'focus:border-slate-400'}`}
                        />
                      </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100 rounded-lg">İptal</button>
          <button onClick={handleSave} className="px-3 py-1.5 text-[11px] font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-700">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function PlannerTable({ title, rows, updateValue, applyJanuaryToAll, removeCustomRow, readOnlySystemRows = false, showMetrics = false, metricsType = 'branding', onEditMetrics, toggleBucket = null, canSwitchBucket = false }) {
  const tableTotalSpend = rows
    .filter(r => r.metricType === 'spend')
    .reduce((sum, r) => sum + rowTotal(r), 0);
  const tableTotalRevenue = rows
    .filter(r => r.metricType === 'revenue')
    .reduce((sum, r) => sum + rowTotal(r), 0);

  return (
    <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden mb-4">
      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/70">
        <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">{title}</p>
      </div>
      <div className="overflow-x-auto">
        <table className={`w-full border-collapse ${showMetrics ? 'min-w-[1750px]' : 'min-w-[1450px]'}`}>
          <thead>
            <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
              <th className={`${DONUK_SUTUN} border-r-slate-600 z-30 bg-slate-800 px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider min-w-[260px]`}>Line Item</th>
              <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider min-w-[90px]">Type</th>
              {MONTHS.map(m => (
                <th key={m.idx} className="px-2 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider min-w-[90px]">{m.label}</th>
              ))}
              <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider min-w-[110px]">Yearly</th>
              {showMetrics && (metricsType === 'performance' ? PERFORMANCE_METRICS : BRANDING_METRICS).map(bm => (
                <th key={bm.key} className="px-2 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider min-w-[85px]" title={metricsType === 'performance' ? 'Performance hedefleri' : 'Report 2 için'}>{bm.label}</th>
              ))}
              <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider min-w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={showMetrics ? 20 : 16} className="px-3 py-6 text-center text-[11px] text-slate-400">No rows in this section</td>
              </tr>
            )}
            {rows.map((row, i) => {
              const tableMetrics = metricsType === 'performance' ? PERFORMANCE_METRICS : BRANDING_METRICS;
              const yearlyMetrics = metricsYearlyTotal(row, tableMetrics);
              return (
              <tr key={row.key} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                <td className={`${DONUK_SUTUN} border-r-slate-200 z-10 px-3 py-2 border-b border-slate-100/80 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} text-[11px] text-slate-700 font-medium`}>
                  <div className="flex items-center gap-2">
                    {SYSTEM_ROW_LOGOS[row.key] ? (
                      <img src={SYSTEM_ROW_LOGOS[row.key]} alt={row.label} className="w-4 h-4 object-contain flex-shrink-0" />
                    ) : (
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                    )}
                    <span>{row.label}</span>
                    {Array.isArray(row.ga4SourceMedium) && row.ga4SourceMedium.length > 0 && (
                      <span
                        className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 flex-shrink-0"
                        title={`GA4 otomatik: ${row.ga4SourceMedium.map(m => `${m.source || '(not set)'} / ${m.medium || '(not set)'}`).join(', ')}`}
                      >
                        GA4
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 border-b border-slate-100/80 text-[10px] text-slate-500 uppercase">{row.metricType}</td>
                {MONTHS.map(m => {
                  // Kova rozeti YALNIZCA elle eklenen harcama satırlarında ve ayrım
                  // yapan markalarda görünür. Tıklayınca o AYIN kovası B↔P değişir;
                  // para yerinde kalır, sadece hangi rapora yazıldığı değişir.
                  const showBucket = canSwitchBucket && !row.isSystem
                    && row.metricType === 'spend' && row.key.startsWith('custom_');
                  const b = showBucket ? plannerBucket(row, m.idx) : null;
                  return (
                  <td key={`${row.key}-${m.idx}`} className="px-2 py-1.5 border-b border-slate-100/80">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.values?.[m.idx] ?? 0}
                      disabled={readOnlySystemRows && row.isSystem}
                      onChange={(e) => updateValue(row.key, m.idx, e.target.value)}
                      className={`w-full text-right text-[11px] text-slate-700 border border-slate-200 rounded px-2 py-1 outline-none tabular-nums ${readOnlySystemRows && row.isSystem ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'focus:border-slate-400'}`}
                    />
                    {showBucket && (
                      <button
                        type="button"
                        onClick={() => toggleBucket(row.key, m.idx)}
                        title={b === 'performance'
                          ? 'Bu ay PERFORMANS sayılıyor — report-3’e yazılır. Branding’e çevirmek için tıkla.'
                          : 'Bu ay BRANDING sayılıyor — report-2’ye yazılır. Performans’a çevirmek için tıkla.'}
                        className={`mt-1 w-full text-[9px] font-semibold rounded py-0.5 border transition-colors ${
                          b === 'performance'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                            : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                        }`}
                      >
                        {b === 'performance' ? 'Performans' : 'Branding'}
                      </button>
                    )}
                  </td>
                  );
                })}
                <td className="px-3 py-2 border-b border-slate-100/80 text-right text-[11px] text-slate-700 font-semibold tabular-nums">{fmtCurrency(rowTotal(row))}</td>
                {showMetrics && (row.metricType === 'spend' ? (
                  tableMetrics.map(bm => (
                    <td key={bm.key} className="px-2 py-1.5 border-b border-slate-100/80 text-right text-[11px] text-slate-600 tabular-nums">
                      {bm.key === 'satis_degeri' ? fmtCurrency(yearlyMetrics[bm.key] || 0) : (yearlyMetrics[bm.key] || 0).toLocaleString('tr-TR')}
                    </td>
                  ))
                ) : (
                  tableMetrics.map(bm => (
                    <td key={bm.key} className="px-2 py-1.5 border-b border-slate-100/80 text-right text-[11px] text-slate-400">–</td>
                  ))
                ))}
                <td className="px-3 py-2 border-b border-slate-100/80">
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {showMetrics && row.metricType === 'spend' && onEditMetrics && (
                      <button
                        type="button"
                        onClick={() => onEditMetrics(row)}
                        className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"
                        title="Aylık metrikleri düzenle"
                      >
                        <Edit3 size={12} /> Metrikler
                      </button>
                    )}
                    {!(readOnlySystemRows && row.isSystem) && (
                      <button onClick={() => applyJanuaryToAll(row.key)} className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50">
                        Jan → All
                      </button>
                    )}
                    {!row.isSystem && (
                      <button onClick={() => removeCustomRow(row.key)} className="text-[10px] p-1 rounded border border-red-200 text-red-500 hover:bg-red-50">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gradient-to-r from-slate-900 to-slate-800">
              <td className={`${DONUK_SUTUN} border-r-slate-700 z-10 bg-slate-900 px-3 py-2.5 text-[11px] font-bold text-white uppercase tracking-wide`}>Monthly Total Spend</td>
              <td className="px-3 py-2.5 text-[10px] text-slate-300">spend</td>
              {MONTHS.map(m => (
                <td key={`spend-${title}-${m.idx}`} className="px-2 py-2.5 text-right text-[11px] font-bold text-white tabular-nums">
                  {fmtCurrency(monthlyTotalByType(rows, 'spend', m.idx))}
                </td>
              ))}
              <td className="px-3 py-2.5 text-right text-[11px] font-bold text-white tabular-nums">{fmtCurrency(tableTotalSpend)}</td>
              {showMetrics && (metricsType === 'performance' ? PERFORMANCE_METRICS : BRANDING_METRICS).map(bm => <td key={`spend-m-${bm.key}`} />)}
              <td />
            </tr>
            <tr className="bg-slate-950">
              <td className={`${DONUK_SUTUN} border-r-slate-700 z-10 bg-slate-950 px-3 py-2.5 text-[11px] font-bold text-white uppercase tracking-wide`}>Monthly Total Revenue</td>
              <td className="px-3 py-2.5 text-[10px] text-slate-300">revenue</td>
              {MONTHS.map(m => (
                <td key={`rev-${title}-${m.idx}`} className="px-2 py-2.5 text-right text-[11px] font-bold text-white tabular-nums">
                  {fmtCurrency(monthlyTotalByType(rows, 'revenue', m.idx))}
                </td>
              ))}
              <td className="px-3 py-2.5 text-right text-[11px] font-bold text-white tabular-nums">{fmtCurrency(tableTotalRevenue)}</td>
              {showMetrics && (metricsType === 'performance' ? PERFORMANCE_METRICS : BRANDING_METRICS).map((bm) => <td key={`rev-m-${bm.key}`} />)}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function CustomReport4() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editMetricsRow, setEditMetricsRow] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);

  const brandId = localStorage.getItem('currentBrandId') || 'unknown-brand';
  const isSplitTargetBrand = SPLIT_TARGET_BRAND_IDS.has(brandId);
  const defaultRows = useMemo(
    () => toDefaultRows(isSplitTargetBrand ? SPLIT_DEFAULT_ROW_DEFS : LEGACY_DEFAULT_ROW_DEFS),
    [isSplitTargetBrand]
  );
  const [rows, setRows] = useState(defaultRows);
  const buildHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
    'X-Brand-ID': brandId,
  });

  const mergeWithDefaultSystemRows = (incomingRows) => {
    const defaults = defaultRows;
    const defaultKeys = new Set(defaults.map(d => d.key));
    const byKey = new Map((incomingRows || []).map(r => [r.key, r]));
    const merged = defaults.map((base) => {
      let existing = byKey.get(base.key);

      // Migration bridge: if split mode has no new row yet, seed from legacy key values.
      if (!existing && isSplitTargetBrand) {
        const legacySeedMap = {
          google_ads_performance: 'google_ads',
          meta_ads_performance: 'meta_ads',
          analytics_revenue_performance: 'analytics_revenue',
        };
        const legacyKey = legacySeedMap[base.key];
        if (legacyKey && byKey.get(legacyKey)) existing = byKey.get(legacyKey);
      }

      if (!existing) return { ...base };
      return {
        ...base,
        values: existing.values || base.values,
        notes: existing.notes || null,
        metricsValues: existing.metricsValues || base.metricsValues || EMPTY_METRICS,
        isSystem: true,
      };
    });
    const legacySystemKeys = new Set(LEGACY_DEFAULT_ROW_DEFS.map(d => d.key));
    const customRows = (incomingRows || []).filter((r) => {
      if (defaultKeys.has(r.key)) return false;
      if (isSplitTargetBrand && legacySystemKeys.has(r.key)) return false;
      return true;
    }).map((r) => ({
      ...r,
      metricsValues: r.metricsValues || EMPTY_METRICS,
    }));
    return [...merged, ...customRows];
  };

  useEffect(() => {
    const fetchRows = async () => {
      if (!brandId || brandId === 'unknown-brand') {
        setRows(defaultRows);
        setDirty(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans?year=${year}`, {
          method: 'GET',
          headers: buildHeaders(),
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));
        const incomingRows = Array.isArray(data?.rows) ? data.rows : [];
        setRows(mergeWithDefaultSystemRows(incomingRows));
        setDirty(false);
      } catch (e) {
        console.error('Failed to fetch monthly target rows:', e);
        toast.error('Failed to load monthly targets');
        setRows(defaultRows);
        setDirty(false);
      } finally {
        setLoading(false);
      }
    };
    fetchRows();
  }, [brandId, year, defaultRows]);

  const totalSpend = useMemo(
    () => rows.filter(r => r.metricType === 'spend').reduce((sum, r) => sum + rowTotal(r), 0),
    [rows]
  );
  const totalRevenue = useMemo(
    () => rows.filter(r => r.metricType === 'revenue').reduce((sum, r) => sum + rowTotal(r), 0),
    [rows]
  );

  const monthlySpend = useMemo(() => {
    const out = {};
    for (const m of MONTHS) {
      out[m.idx] = rows
        .filter(r => r.metricType === 'spend')
        .reduce((sum, r) => sum + Number(r.values?.[m.idx] || 0), 0);
    }
    return out;
  }, [rows]);

  const monthlyRevenue = useMemo(() => {
    const out = {};
    for (const m of MONTHS) {
      out[m.idx] = rows
        .filter(r => r.metricType === 'revenue')
        .reduce((sum, r) => sum + Number(r.values?.[m.idx] || 0), 0);
    }
    return out;
  }, [rows]);

  const performanceRows = useMemo(
    () => rows.filter(r => r.key.includes('_performance')),
    [rows]
  );
  const brandingRows = useMemo(
    () => rows.filter(r => r.key.includes('_branding')),
    [rows]
  );
  const totalRows = useMemo(() => {
    if (!isSplitTargetBrand) return rows;
    const sumByPrefix = (prefix) => Object.fromEntries(
      MONTHS.map((m) => {
        const total = rows
          .filter(r => r.key.startsWith(prefix))
          .reduce((sum, r) => sum + Number(r.values?.[m.idx] || 0), 0);
        return [m.idx, total];
      })
    );
    const customRowsForTotals = rows
      .filter(r => r.key.startsWith('custom_'))
      .map((r) => ({
        ...r,
        key: `total_${r.key}`,
        label: `${r.label} (Manual)`,
        // Total Targets table is intended to be a read-only rollup view.
        isSystem: true,
      }));

    return [
      {
        key: 'google_ads_total',
        label: 'Google Ads - Total Budget',
        metricType: 'spend',
        isSystem: true,
        values: sumByPrefix('google_ads_'),
      },
      {
        key: 'meta_ads_total',
        label: 'Meta Ads - Total Budget',
        metricType: 'spend',
        isSystem: true,
        values: sumByPrefix('meta_ads_'),
      },
      {
        key: 'analytics_revenue_total',
        label: 'Analytics - Total Revenue Target',
        metricType: 'revenue',
        isSystem: true,
        values: sumByPrefix('analytics_revenue_'),
      },
      ...customRowsForTotals,
    ];
  }, [isSplitTargetBrand, rows]);

  const updateValue = (rowKey, month, value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) && num >= 0 ? num : 0;
    setRows(prev => prev.map(r => (
      r.key === rowKey
        ? { ...r, values: { ...r.values, [month]: safe } }
        : r
    )));
    setDirty(true);
  };

  const saveMetricsForRow = (rowKey, metricsValues) => {
    setRows(prev => prev.map(r => r.key === rowKey ? { ...r, metricsValues } : r));
    setDirty(true);
  };

  const savePlanWithRows = async (rowsToSave) => {
    if (!brandId || brandId === 'unknown-brand') return;
    try {
      setLoading(true);
      const payload = {
        year,
        rows: rowsToSave.map((r) => ({
          key: r.key,
          label: r.label,
          metricType: r.metricType === 'revenue' ? 'revenue' : 'spend',
          isSystem: !!r.isSystem,
          notes: r.notes || null,
          values: r.values || {},
          buckets: r.buckets || {},
          metricsValues: r.metricsValues || {},
          ga4SourceMedium: Array.isArray(r.ga4SourceMedium) ? r.ga4SourceMedium : [],
        })),
      };
      const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans/bulk`, {
        method: 'PUT',
        headers: buildHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(rowsToSave);
      setDirty(false);
      toast.success('Metrikler kaydedildi');
    } catch (e) {
      console.error('Save metrics error:', e);
      toast.error('Kaydetme başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Bir AYIN kovasını branding ↔ performans çevirir. Tutar yerinde kalır; değişen
  // tek şey o ayın hangi rapora (report-2 mi report-3 mü) yazılacağı.
  // Yandex/Bing gibi ay ay yön değiştiren elle girilen kanallar için — eskiden bunun
  // tek yolu ikinci bir satır açıp parayı elle taşımaktı.
  const toggleBucket = (rowKey, month) => {
    setRows(prev => prev.map(r => {
      if (r.key !== rowKey) return r;
      const cur = plannerBucket(r, month);
      const next = cur === 'performance' ? 'branding' : 'performance';
      return { ...r, buckets: { ...(r.buckets || {}), [month]: next } };
    }));
    setDirty(true);
  };

  const applyJanuaryToAll = (rowKey) => {
    setRows(prev => prev.map(r => {
      if (r.key !== rowKey) return r;
      const jan = Number(r.values?.[1] || 0);
      return { ...r, values: Object.fromEntries(MONTHS.map(m => [m.idx, jan])) };
    }));
    setDirty(true);
  };

  const removeCustomRow = (rowKey) => {
    setRows(prev => prev.filter(r => r.key !== rowKey));
    setDirty(true);
  };

  const savePlan = async () => {
    if (!brandId || brandId === 'unknown-brand') {
      toast.error('Please select a brand first');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        year,
        rows: rows.map((r) => ({
          key: r.key,
          label: r.label,
          metricType: r.metricType === 'revenue' ? 'revenue' : 'spend',
          isSystem: !!r.isSystem,
          notes: r.notes || null,
          values: r.values || {},
          buckets: r.buckets || {},
          metricsValues: r.metricsValues || {},
          ga4SourceMedium: Array.isArray(r.ga4SourceMedium) ? r.ga4SourceMedium : [],
        })),
      };
      const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans/bulk`, {
        method: 'PUT',
        headers: buildHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      setDirty(false);
      toast.success('Monthly targets saved');
    } catch (e) {
      console.error('Failed to save monthly target rows:', e);
      toast.error(`Save failed: ${e.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const resetPlan = async () => {
    if (!brandId || brandId === 'unknown-brand') {
      toast.error('Please select a brand first');
      setRows(defaultRows);
      setDirty(false);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans?year=${year}`, {
        method: 'DELETE',
        headers: buildHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      setRows(defaultRows);
      setDirty(false);
      toast.success('Monthly targets reset');
    } catch (e) {
      console.error('Failed to reset monthly target rows:', e);
      toast.error(`Reset failed: ${e.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-12">
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 tracking-tight">Monthly Target Planner</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {isSplitTargetBrand
              ? 'Plan monthly performance vs branding budgets and revenue targets'
              : 'Plan monthly spend targets for Google/Meta and revenue targets for Analytics'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm">
            <Calendar size={12} className="text-slate-400" />
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="text-[11px] text-slate-600 bg-transparent border-none outline-none cursor-pointer"
            >
              {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button disabled={loading} onClick={savePlan} className="flex items-center gap-1.5 bg-slate-800 text-white rounded-lg px-3 py-1.5 text-[11px] font-medium hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-60">
            <Save size={12} /> {loading ? 'Saving...' : 'Save'}
          </button>
          <button disabled={loading} onClick={resetPlan} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60">
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-lg border border-slate-200/80 px-4 py-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Yearly Total Spend</p>
          <p className="text-lg font-bold text-slate-800 mt-0.5 tabular-nums">{fmtCurrency(totalSpend)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200/80 px-4 py-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Yearly Revenue Target</p>
          <p className="text-lg font-bold text-slate-800 mt-0.5 tabular-nums">{fmtCurrency(totalRevenue)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200/80 px-4 py-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Line Items</p>
          <p className="text-lg font-bold text-slate-800 mt-0.5 tabular-nums">{rows.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200/80 px-4 py-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Status</p>
          <p className={`text-lg font-bold mt-0.5 tabular-nums ${dirty ? 'text-amber-600' : 'text-emerald-600'}`}>
            {dirty ? 'Unsaved changes' : 'Saved'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden mb-4">
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/70">
          <button
            onClick={() => setAddModalOpen(true)}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-700"
          >
            <Plus size={12} /> Add Manual Line
          </button>
        </div>
      </div>

      {addModalOpen && (
        <AddManualLineModal
          isSplitTargetBrand={isSplitTargetBrand}
          brandId={brandId}
          year={year}
          onClose={() => setAddModalOpen(false)}
          onSave={(newRow) => {
            const updated = [...rows, newRow];
            setRows(updated);
            setAddModalOpen(false);
            savePlanWithRows(updated);
          }}
        />
      )}

      {editMetricsRow && (
        <EditMetricsModal
          row={editMetricsRow}
          brandId={brandId}
          year={year}
          onClose={() => setEditMetricsRow(null)}
          onSave={({ metricsValues, ga4SourceMedium }) => {
            const updated = rows.map(r => r.key === editMetricsRow.key
              ? { ...r, metricsValues, ga4SourceMedium: Array.isArray(ga4SourceMedium) ? ga4SourceMedium : (r.ga4SourceMedium || []) }
              : r);
            setRows(updated);
            setDirty(false);
            setEditMetricsRow(null);
            savePlanWithRows(updated);
          }}
        />
      )}

      {isSplitTargetBrand ? (
        <>
          <PlannerTable
            title="Performance Targets"
            rows={performanceRows}
            updateValue={updateValue}
            applyJanuaryToAll={applyJanuaryToAll}
            removeCustomRow={removeCustomRow}
            showMetrics
            metricsType="performance"
            onEditMetrics={(row) => setEditMetricsRow(row)}
            toggleBucket={toggleBucket}
            canSwitchBucket
          />
          <PlannerTable
            title="Branding Targets"
            rows={brandingRows}
            updateValue={updateValue}
            applyJanuaryToAll={applyJanuaryToAll}
            removeCustomRow={removeCustomRow}
            showMetrics
            metricsType="branding"
            onEditMetrics={(row) => setEditMetricsRow(row)}
            toggleBucket={toggleBucket}
            canSwitchBucket
          />
          <PlannerTable
            title="Total Targets"
            rows={totalRows}
            updateValue={updateValue}
            applyJanuaryToAll={applyJanuaryToAll}
            removeCustomRow={removeCustomRow}
            readOnlySystemRows
          />
        </>
      ) : (
        <PlannerTable
          title="Monthly Plan"
          rows={rows}
          updateValue={updateValue}
          applyJanuaryToAll={applyJanuaryToAll}
          removeCustomRow={removeCustomRow}
          showMetrics
          onEditMetrics={(row) => setEditMetricsRow(row)}
        />
      )}
    </div>
  );
}

